import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Groups as GroupsIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    alpha,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ContentContainer from '@libComponents/ContentContainer';
import FilterInput from '@libComponents/FilterInput';
import Pagination from '@libComponents/Pagination';
import { apiClient } from '@libs/apiRequest';
import { theme } from 'app/Theme';
import BasicMenu from 'common/components/tableHandlar/rowhandlar';
import SelectBranch from '../../employee/components/SelectBranch';
import SelectDepartment from '../../employee/components/SelectDepartment';
import SelectDesignation from '../../employee/components/SelectDesignation';
import SelectEmployeeCategory from '../../employee/components/SelectEmployeeCategory';
import SelectGender from '../../employee/components/SelectGender';
import SelectShift from '../../employee/components/SelectShift';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import StatusChip from './StatusChip';
import TeamDialog from './TeamDialog';

const DEFAULT_UNASSIGNED_COLUMNS = {
    name: {
        label: 'Full Name',
        isVisible: true,
        disabledChange: true,
        minWidth: 200,
        sortable: true
    },
    departmentName: {
        label: 'Department',
        isVisible: true,
        disabledChange: false,
        minWidth: 180,
        sortable: true
    },
    designationName: {
        label: 'Designation',
        isVisible: true,
        disabledChange: false,
        minWidth: 180,
        sortable: true
    },
    email: {
        label: 'Email',
        isVisible: true,
        disabledChange: true,
        minWidth: 220,
        sortable: true
    },
    branchName: {
        label: 'Branch',
        isVisible: true,
        disabledChange: false,
        minWidth: 160,
        sortable: true
    },
    joiningDate: {
        label: 'Joining Date',
        isVisible: true,
        disabledChange: false,
        minWidth: 140,
        sortable: true
    },
    employeeId: {
        label: 'Employee Id',
        isVisible: true,
        disabledChange: false,
        minWidth: 140,
        sortable: true
    }
};

const formatPersonName = (entry) => {
    if (!entry) return '';
    const first = entry.profile?.firstname || entry.firstname || '';
    const middle = entry.profile?.middlename || entry.middlename || '';
    const last = entry.profile?.lastname || entry.lastname || '';
    return [first, middle, last].filter(Boolean).join(' ');
};

const TabPanel = ({ children, value, index }) => (
    <Box role='tabpanel' hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
    </Box>
);

const createEmptyMember = () => ({ employeeEmail: '', role: '', skills: [] });

const normalizeSkillLabel = (skill) => {
    if (!skill) return '';
    if (typeof skill === 'string') return skill.trim();
    return String(skill.name || skill.label || '').trim();
};

const normalizeSkillList = (skills = []) => {
    const seen = new Map();
    (skills || []).forEach((skill) => {
        const label = normalizeSkillLabel(skill);
        if (!label) return;
        const key = label.toLowerCase();
        if (!seen.has(key)) seen.set(key, label);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
};

const memberSkillNames = (member) => normalizeSkillList(member?.skills || []);

const TeamMemberDialog = ({
    open,
    mode,
    teamId,
    member,
    employeeOptions,
    employeeOptionsLoading,
    skillOptions,
    onClose,
    onSaved
}) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState(createEmptyMember());

    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && member) {
            setForm({
                employeeEmail: member.employeeEmail || '',
                role: member.role || '',
                skills: memberSkillNames(member)
            });
        } else {
            setForm(createEmptyMember());
        }
    }, [open, mode, member]);

    const handleClose = () => {
        setForm(createEmptyMember());
        onClose();
    };

    const handleSubmit = () => {
        if (!teamId || !form.employeeEmail.trim()) return;
        const payload = {
            employeeEmail: form.employeeEmail.trim(),
            role: form.role.trim() || undefined,
            skills: normalizeSkillList(form.skills)
        };

        if (mode === 'edit') {
            dispatch(
                actions.updateMember(teamId, member.employeeEmail, payload)
            );
        } else {
            dispatch(actions.addMember(teamId, payload));
        }

        onSaved?.();
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box component='span'>
                    {mode === 'edit' ? 'Update Member' : 'Add Member'}
                </Box>
                <IconButton size='small' onClick={handleClose}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Autocomplete
                        size='small'
                        fullWidth
                        options={
                            Array.isArray(employeeOptions)
                                ? employeeOptions
                                : []
                        }
                        getOptionLabel={(option) => {
                            if (!option) return '';
                            const name = [
                                option.firstname,
                                option.middlename,
                                option.lastname
                            ]
                                .filter(Boolean)
                                .join(' ')
                                .trim();
                            const email =
                                option.user?.email || option.employeeId || '';
                            return name ? `${name} (${email})` : email;
                        }}
                        isOptionEqualToValue={(opt, val) =>
                            (opt.user?.email || opt.employeeId) ===
                            (val.user?.email || val.employeeId)
                        }
                        value={
                            employeeOptions.find(
                                (e) =>
                                    (e.user?.email || e.employeeId) ===
                                    form.employeeEmail
                            ) || null
                        }
                        onChange={(_, newValue) => {
                            const email = newValue
                                ? newValue.user?.email ||
                                  newValue.employeeId ||
                                  ''
                                : '';
                            setForm((p) => ({ ...p, employeeEmail: email }));
                        }}
                        loading={employeeOptionsLoading}
                        disabled={mode === 'edit'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='Employee'
                                placeholder='Search by name or email'
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {employeeOptionsLoading ? (
                                                <CircularProgress
                                                    color='inherit'
                                                    size={18}
                                                />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                    <TextField
                        label='Role'
                        size='small'
                        fullWidth
                        value={form.role}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, role: e.target.value }))
                        }
                    />
                    <Autocomplete
                        multiple
                        freeSolo
                        size='small'
                        fullWidth
                        options={skillOptions}
                        value={form.skills}
                        onChange={(_, newValue) =>
                            setForm((p) => ({
                                ...p,
                                skills: normalizeSkillList(newValue)
                            }))
                        }
                        filterSelectedOptions
                        getOptionLabel={(option) => normalizeSkillLabel(option)}
                        isOptionEqualToValue={(option, value) =>
                            normalizeSkillLabel(option) ===
                            normalizeSkillLabel(value)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='Skills'
                                placeholder='Type or select skills'
                            />
                        )}
                        sx={{ display: 'none' }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={handleClose} startIcon={<CloseIcon />}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant='contained'
                    startIcon={<SaveIcon />}
                >
                    {mode === 'edit' ? 'Update' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TeamDetailsPage = () => {
    const { id: teamIdParam } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const detail = useSelector(selectors.detailSelector);
    const loading = useSelector(selectors.detailLoadingSelector);
    const employeeOptions =
        useSelector(selectors.employeeOptionsSelector) || [];
    const employeeOptionsLoading = useSelector(
        selectors.employeeOptionsLoadingSelector
    );

    const [tab, setTab] = useState(0);
    const [memberDialogOpen, setMemberDialogOpen] = useState(false);
    const [memberDialogMode, setMemberDialogMode] = useState('add');
    const [selectedMember, setSelectedMember] = useState(null);

    // Unassigned employees state & filters
    const [unassignedTableData, setUnassignedTableData] = useState(() => {
        try {
            const saved = localStorage.getItem(
                'ehrms_unassigned_table_columns'
            );
            if (saved) {
                const parsed = JSON.parse(saved);
                return Object.keys(DEFAULT_UNASSIGNED_COLUMNS).reduce(
                    (acc, key) => {
                        acc[key] = {
                            ...DEFAULT_UNASSIGNED_COLUMNS[key],
                            ...(parsed[key]
                                ? { isVisible: parsed[key].isVisible }
                                : {})
                        };
                        return acc;
                    },
                    {}
                );
            }
        } catch (e) {
            console.error(
                'Failed to load unassigned table columns from localStorage',
                e
            );
        }
        return DEFAULT_UNASSIGNED_COLUMNS;
    });

    useEffect(() => {
        try {
            localStorage.setItem(
                'ehrms_unassigned_table_columns',
                JSON.stringify(unassignedTableData)
            );
        } catch (e) {
            console.error(
                'Failed to save unassigned table columns to localStorage',
                e
            );
        }
    }, [unassignedTableData]);

    const unassignedColumns = Object.entries(unassignedTableData)
        .filter(([_, col]) => col.isVisible)
        .map(([id, col]) => ({
            id,
            label: col.label,
            sortable: col.sortable,
            minWidth: col.minWidth
        }));

    const [unassignedEmployees, setUnassignedEmployees] = useState([]);
    const [unassignedLoading, setUnassignedLoading] = useState(false);
    const [unassignedTotal, setUnassignedTotal] = useState(0);
    const [unassignedPageSize, setUnassignedPageSize] = useState(10);
    const [unassignedCurrentPage, setUnassignedCurrentPage] = useState(1);
    const [unassignedSearch, setUnassignedSearch] = useState('');
    const [unassignedBranch, setUnassignedBranch] = useState(null);
    const [unassignedDepartment, setUnassignedDepartment] = useState(null);
    const [unassignedDesignation, setUnassignedDesignation] = useState(null);
    const [unassignedCategory, setUnassignedCategory] = useState(null);
    const [unassignedGender, setUnassignedGender] = useState(null);
    const [unassignedShift, setUnassignedShift] = useState(null);
    const [unassignedSort, setUnassignedSort] = useState({
        sortBy: 'name',
        sortDescending: false
    });
    const isUnassigned = teamIdParam === 'unassigned';
    const initialUnassignedUrlSynced = useRef(false);

    // Sync URL search params to Unassigned state on mount
    useEffect(() => {
        if (isUnassigned && !initialUnassignedUrlSynced.current) {
            initialUnassignedUrlSynced.current = true;
            const search =
                searchParams.get('search-term') ||
                searchParams.get('search') ||
                '';
            const branch = searchParams.get('branch') || null;
            const department = searchParams.get('department') || null;
            const designation = searchParams.get('designation') || null;
            const category = searchParams.get('category') || null;
            const gender = searchParams.get('gender') || null;
            const shift = searchParams.get('shift') || null;
            const page = parseInt(
                searchParams.get('page') || searchParams.get('offset') || '1',
                10
            );
            const limit = parseInt(searchParams.get('limit') || '10', 10);
            const sortBy =
                searchParams.get('sort-by') ||
                searchParams.get('sortBy') ||
                'name';
            const sortDescending =
                searchParams.get('sort-descending') === 'true' ||
                searchParams.get('sortDescending') === 'true';

            setUnassignedSearch(search);
            setUnassignedBranch(branch);
            setUnassignedDepartment(department);
            setUnassignedDesignation(designation);
            setUnassignedCategory(category);
            setUnassignedGender(gender);
            setUnassignedShift(shift);
            setUnassignedCurrentPage(page);
            setUnassignedPageSize(limit);
            setUnassignedSort({ sortBy, sortDescending });
        }
    }, [isUnassigned, searchParams]);

    // Sync Unassigned state to URL search params
    useEffect(() => {
        if (isUnassigned && initialUnassignedUrlSynced.current) {
            const params = new URLSearchParams();
            if (unassignedSearch) params.set('search-term', unassignedSearch);
            if (unassignedBranch) params.set('branch', unassignedBranch);
            if (unassignedDepartment)
                params.set('department', unassignedDepartment);
            if (unassignedDesignation)
                params.set('designation', unassignedDesignation);
            if (unassignedCategory) params.set('category', unassignedCategory);
            if (unassignedGender) params.set('gender', unassignedGender);
            if (unassignedShift) params.set('shift', unassignedShift);
            if (unassignedCurrentPage > 1)
                params.set('page', String(unassignedCurrentPage));
            if (unassignedPageSize !== 10)
                params.set('limit', String(unassignedPageSize));
            if (unassignedSort.sortBy)
                params.set('sort-by', unassignedSort.sortBy);
            if (unassignedSort.sortDescending)
                params.set('sort-descending', 'true');

            setSearchParams(params, { replace: true });
        }
    }, [
        isUnassigned,
        unassignedSearch,
        unassignedBranch,
        unassignedDepartment,
        unassignedDesignation,
        unassignedCategory,
        unassignedGender,
        unassignedShift,
        unassignedCurrentPage,
        unassignedPageSize,
        unassignedSort,
        setSearchParams
    ]);

    const fetchUnassigned = useCallback(async () => {
        if (!isUnassigned) return;
        setUnassignedLoading(true);
        try {
            const params = {
                limit: unassignedPageSize,
                offset: unassignedCurrentPage,
                'search-term': unassignedSearch,
                branch: unassignedBranch,
                department: unassignedDepartment,
                designation: unassignedDesignation,
                category: unassignedCategory,
                gender: unassignedGender,
                shift: unassignedShift,
                'sort-by': unassignedSort.sortBy,
                'sort-descending': unassignedSort.sortDescending
            };
            Object.keys(params).forEach((k) => {
                if (
                    params[k] === null ||
                    params[k] === undefined ||
                    params[k] === ''
                )
                    delete params[k];
            });
            const response = await apiClient.get('/team/unassigned/employees', {
                params
            });
            const respData = response.data;
            setUnassignedEmployees(respData?.data || []);
            setUnassignedTotal(parseInt(respData?.total) || 0);
        } catch {
            setUnassignedEmployees([]);
            setUnassignedTotal(0);
        } finally {
            setUnassignedLoading(false);
        }
    }, [
        isUnassigned,
        unassignedPageSize,
        unassignedCurrentPage,
        unassignedSearch,
        unassignedBranch,
        unassignedDepartment,
        unassignedDesignation,
        unassignedCategory,
        unassignedGender,
        unassignedShift,
        unassignedSort
    ]);

    useEffect(() => {
        if (isUnassigned) {
            fetchUnassigned();
        } else if (teamIdParam) {
            dispatch(actions.fetchTeamDetail(teamIdParam));
            dispatch(actions.fetchEmployeeOptions());
        }
        return () => {
            if (!isUnassigned) dispatch(actions.clearTeamDetail());
        };
    }, [dispatch, teamIdParam, isUnassigned, fetchUnassigned]);

    const team = detail;
    const teamId = team?.id || teamIdParam;
    const skillOptions = normalizeSkillList(
        (team?.members || []).flatMap((member) => memberSkillNames(member))
    );

    const handleBack = () => navigate('/teams');
    const handleEdit = () => team && dispatch(actions.openTeamDialog(team));
    const handleDelete = () => team && dispatch(actions.openDeleteDialog(team));

    const handleRemoveMember = (email) =>
        dispatch(actions.removeMember(teamId, email));
    const handleOpenAddMember = () => {
        setSelectedMember(null);
        setMemberDialogMode('add');
        setMemberDialogOpen(true);
    };
    const handleOpenEditMember = (m) => {
        setSelectedMember(m);
        setMemberDialogMode('edit');
        setMemberDialogOpen(true);
    };
    const handleCloseMemberDialog = () => {
        setMemberDialogOpen(false);
        setSelectedMember(null);
    };

    // Unassigned handlers
    const handleUnassignedSearch = (value) => {
        setUnassignedSearch(value);
        setUnassignedCurrentPage(1);
    };

    const handleUnassignedPageChange = (event, newPage) => {
        setUnassignedCurrentPage(newPage);
    };

    const handleUnassignedRowsPerPageChange = (newPageSize) => {
        setUnassignedPageSize(newPageSize);
        setUnassignedCurrentPage(1);
    };

    const handleUnassignedSort = (columnId) => {
        const same = unassignedSort.sortBy === columnId;
        setUnassignedSort({
            sortBy: columnId,
            sortDescending: same ? !unassignedSort.sortDescending : false
        });
        setUnassignedCurrentPage(1);
    };

    const handleClearUnassignedFilters = () => {
        setUnassignedSearch('');
        setUnassignedBranch(null);
        setUnassignedDepartment(null);
        setUnassignedDesignation(null);
        setUnassignedCategory(null);
        setUnassignedGender(null);
        setUnassignedShift(null);
        setUnassignedCurrentPage(1);
    };

    const headerControls = (
        <Stack
            direction='row'
            spacing={1.5}
            flexWrap='wrap'
            alignItems='center'
            justifyContent='flex-end'
        >
            <Button
                variant='outlined'
                size='small'
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ height: '36px' }}
            >
                Back to Teams
            </Button>
            <Button
                variant='outlined'
                size='small'
                startIcon={<EditIcon />}
                onClick={handleEdit}
                disabled={!team}
                sx={{ height: '36px' }}
            >
                Edit
            </Button>
            <Button
                variant='outlined'
                color='error'
                size='small'
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!team}
                sx={{ height: '36px' }}
            >
                Delete
            </Button>
        </Stack>
    );

    // Unassigned page
    if (isUnassigned) {
        return (
            <ContentContainer
                title=''
                AdornmentComponent={
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        {/* Top Row: Title & Search */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Typography
                                variant='h5'
                                sx={{
                                    fontWeight: 600,
                                    color: (theme) =>
                                        theme.palette.text.primary,
                                    '@media (max-width: 1024px)': {
                                        fontSize: '1.25rem'
                                    }
                                }}
                            >
                                Unassigned
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: '100%', sm: 300 },
                                        background: (theme) =>
                                            theme.palette.common.paper
                                    }}
                                >
                                    <FilterInput
                                        name='unassigned-search'
                                        handleSearch={handleUnassignedSearch}
                                        value={unassignedSearch}
                                        placeholder='Search'
                                    />
                                </Box>
                                <BasicMenu
                                    tableData={unassignedTableData}
                                    setTableData={setUnassignedTableData}
                                />
                            </Box>
                        </Box>

                        {/* Bottom Row: Filter Dropdowns */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'end',
                                gap: 1,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    justifyContent: 'end'
                                }}
                            >
                                <Typography
                                    variant='p'
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                        mr: 1
                                    }}
                                >
                                    Filters:
                                </Typography>
                                <SelectBranch
                                    input={{
                                        name: 'branch',
                                        onChange: (v) => {
                                            setUnassignedBranch(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedBranch
                                    }}
                                />
                                <SelectDepartment
                                    input={{
                                        name: 'department',
                                        onChange: (v) => {
                                            setUnassignedDepartment(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedDepartment
                                    }}
                                />
                                <SelectDesignation
                                    input={{
                                        name: 'designation',
                                        onChange: (v) => {
                                            setUnassignedDesignation(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedDesignation
                                    }}
                                />
                                <SelectEmployeeCategory
                                    input={{
                                        name: 'employeeCategory',
                                        onChange: (v) => {
                                            setUnassignedCategory(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedCategory
                                    }}
                                />
                                <SelectGender
                                    input={{
                                        name: 'gender',
                                        onChange: (v) => {
                                            setUnassignedGender(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedGender
                                    }}
                                />
                                <SelectShift
                                    input={{
                                        name: 'shift',
                                        onChange: (v) => {
                                            setUnassignedShift(v);
                                            setUnassignedCurrentPage(1);
                                        },
                                        value: unassignedShift
                                    }}
                                />
                                <Button
                                    variant='outlined'
                                    onClick={handleClearUnassignedFilters}
                                    startIcon={<RefreshIcon />}
                                    size='small'
                                    sx={{
                                        height: '34px',
                                        minHeight: '34px'
                                    }}
                                >
                                    RESET
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                }
            >
                <Box sx={{ width: '100%' }}>
                    {/* Table matching Employee List */}
                    <TableContainer
                        component={Box}
                        sx={{
                            position: 'relative',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            minHeight: 200
                        }}
                    >
                        {unassignedLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: (theme) =>
                                        alpha(
                                            theme.palette.background.paper,
                                            0.6
                                        ),
                                    zIndex: 10
                                }}
                            >
                                <CircularProgress size={36} />
                            </Box>
                        )}
                        <Table
                            sx={{
                                tableLayout: { xs: 'auto', sm: 'fixed' },
                                minWidth: 'max-content'
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {unassignedColumns.map((col) => (
                                        <TableCell
                                            key={col.id}
                                            align='left'
                                            style={{ minWidth: col.minWidth }}
                                            sx={{
                                                fontWeight: 600,
                                                backgroundColor: (theme) =>
                                                    theme.palette.background
                                                        .childHeader,
                                                borderBottom: 1,
                                                borderBottomColor: 'divider'
                                            }}
                                        >
                                            {col.sortable ? (
                                                <TableSortLabel
                                                    active={
                                                        unassignedSort.sortBy ===
                                                        col.id
                                                    }
                                                    direction={
                                                        unassignedSort.sortBy ===
                                                            col.id &&
                                                        unassignedSort.sortDescending
                                                            ? 'desc'
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleUnassignedSort(
                                                            col.id
                                                        )
                                                    }
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            ) : (
                                                col.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!unassignedLoading &&
                                unassignedEmployees.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={
                                                unassignedColumns.length || 1
                                            }
                                            align='center'
                                            sx={{ py: 8 }}
                                        >
                                            <Typography
                                                variant='body2'
                                                color='text.secondary'
                                            >
                                                No unassigned employees found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    unassignedEmployees.map((emp, idx) => {
                                        const name = [
                                            emp.firstname,
                                            emp.middlename,
                                            emp.lastname
                                        ]
                                            .filter(Boolean)
                                            .join(' ');
                                        const fullName = name || emp.email;
                                        return (
                                            <TableRow
                                                key={emp.employeeId || idx}
                                                hover
                                                sx={{
                                                    '&:last-child td': {
                                                        border: 0
                                                    }
                                                }}
                                            >
                                                {unassignedTableData.name
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 1.5,
                                                                minWidth: 0
                                                            }}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    fontSize: 14,
                                                                    bgcolor: (
                                                                        theme
                                                                    ) =>
                                                                        theme
                                                                            .palette
                                                                            .primary
                                                                            .main
                                                                }}
                                                            >
                                                                {fullName
                                                                    ? fullName
                                                                          .split(
                                                                              ' '
                                                                          )
                                                                          .map(
                                                                              (
                                                                                  n
                                                                              ) =>
                                                                                  n[0]
                                                                          )
                                                                          .filter(
                                                                              Boolean
                                                                          )
                                                                          .slice(
                                                                              0,
                                                                              2
                                                                          )
                                                                          .join(
                                                                              ''
                                                                          )
                                                                          .toUpperCase()
                                                                    : '?'}
                                                            </Avatar>
                                                            <Typography
                                                                variant='body2'
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    overflow:
                                                                        'hidden',
                                                                    textOverflow:
                                                                        'ellipsis'
                                                                }}
                                                            >
                                                                {fullName}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData
                                                    .departmentName
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.departmentName ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData
                                                    .designationName
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.designationName ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData.email
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.email}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData.branchName
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.branchName ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData.joiningDate
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.joiningDate
                                                                ? dayjs(
                                                                      emp.joiningDate
                                                                  ).format(
                                                                      'DD MMM YYYY'
                                                                  )
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                {unassignedTableData.employeeId
                                                    ?.isVisible && (
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis'
                                                            }}
                                                        >
                                                            {emp.employeeId ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {unassignedTotal > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Pagination
                                count={Math.ceil(
                                    unassignedTotal / unassignedPageSize
                                )}
                                onChange={handleUnassignedPageChange}
                                page={unassignedCurrentPage}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                                isRowsPerPageVarient={true}
                                rowsPerPageProps={{
                                    name: 'unassigned-rows',
                                    onChange: handleUnassignedRowsPerPageChange,
                                    value: unassignedPageSize
                                }}
                                rowsPerPageOptions={[
                                    { label: '10', value: 10 },
                                    { label: '25', value: 25 },
                                    { label: '50', value: 50 },
                                    { label: '100', value: 100 }
                                ]}
                            />
                        </Box>
                    )}
                </Box>
            </ContentContainer>
        );
    }

    if (loading && !team) {
        return null;
    }

    if (!loading && !team) {
        return (
            <ContentContainer
                title='Team Not Found'
                headerBg={theme.palette.background.mainHeader}
                AdornmentComponent={
                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ height: '36px' }}
                    >
                        Back to Teams
                    </Button>
                }
            >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant='body1' color='text.secondary'>
                        The requested team could not be found.
                    </Typography>
                </Box>
            </ContentContainer>
        );
    }

    return (
        <>
            <ContentContainer
                title={team?.name || 'Team Details'}
                headerBg={theme.palette.background.mainHeader}
                AdornmentComponent={headerControls}
            >
                <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
                    {/* Summary card */}
                    <Paper
                        variant='outlined'
                        sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 3,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap'
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                width: 56,
                                height: 56
                            }}
                        >
                            <GroupsIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                {team?.name || '-'}
                            </Typography>
                            {team?.description && (
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    {team.description}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction='row' spacing={2} alignItems='center'>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Members
                                </Typography>
                                <Typography
                                    variant='h6'
                                    sx={{ fontWeight: 700 }}
                                >
                                    {team?.members?.length || 0}
                                </Typography>
                            </Box>
                            <Box>
                                <StatusChip active={!!team?.isActive} />
                            </Box>
                        </Stack>
                    </Paper>

                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                        variant='scrollable'
                        allowScrollButtonsMobile
                    >
                        <Tab label='Overview' />
                        <Tab
                            label={`Members (${team?.members?.length || 0})`}
                        />
                    </Tabs>

                    <TabPanel value={tab} index={0}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Name
                                </Typography>
                                <Typography>{team?.name || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Status
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <StatusChip active={!!team?.isActive} />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Description
                                </Typography>
                                <Typography>
                                    {team?.description || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Created At
                                </Typography>
                                <Typography>
                                    {team?.createdAt
                                        ? dayjs(team.createdAt).format(
                                              'MMM DD, YYYY HH:mm'
                                          )
                                        : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Updated At
                                </Typography>
                                <Typography>
                                    {team?.updatedAt
                                        ? dayjs(team.updatedAt).format(
                                              'MMM DD, YYYY HH:mm'
                                          )
                                        : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tab} index={1}>
                        <Paper
                            variant='outlined'
                            sx={{
                                p: 2,
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Box>
                                <Typography variant='subtitle2'>
                                    Members
                                </Typography>
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    Add a new member or edit an existing one in
                                    a dialog.
                                </Typography>
                            </Box>
                            <Button
                                startIcon={<AddIcon />}
                                variant='contained'
                                onClick={handleOpenAddMember}
                            >
                                Add Member
                            </Button>
                        </Paper>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1
                            }}
                        >
                            <List dense disablePadding>
                                {(team?.members || []).map((m, idx) => {
                                    const name = formatPersonName(m);
                                    return (
                                        <Box key={m.employeeEmail || idx}>
                                            <ListItem
                                                secondaryAction={
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Tooltip title='Edit Member'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() =>
                                                                    handleOpenEditMember(
                                                                        m
                                                                    )
                                                                }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title='Remove'>
                                                            <IconButton
                                                                size='small'
                                                                color='error'
                                                                onClick={() =>
                                                                    handleRemoveMember(
                                                                        m.employeeEmail
                                                                    )
                                                                }
                                                            >
                                                                <DeleteIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1,
                                                                alignItems:
                                                                    'center',
                                                                flexWrap: 'wrap'
                                                            }}
                                                        >
                                                            <Typography
                                                                component='span'
                                                                sx={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {name ||
                                                                    m.employeeEmail}
                                                            </Typography>
                                                            {name && (
                                                                <Typography
                                                                    component='span'
                                                                    variant='body2'
                                                                    color='text.secondary'
                                                                >
                                                                    {
                                                                        m.employeeEmail
                                                                    }
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Stack
                                                            spacing={0.75}
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            <Typography
                                                                variant='body2'
                                                                color='text.secondary'
                                                            >
                                                                {m.role ||
                                                                    'No role'}
                                                            </Typography>
                                                            <Stack
                                                                direction='row'
                                                                spacing={0.5}
                                                                flexWrap='wrap'
                                                                useFlexGap
                                                                sx={{
                                                                    display:
                                                                        'none'
                                                                }}
                                                            >
                                                                {memberSkillNames(
                                                                    m
                                                                ).length > 0 ? (
                                                                    memberSkillNames(
                                                                        m
                                                                    ).map(
                                                                        (
                                                                            skill
                                                                        ) => (
                                                                            <Chip
                                                                                key={
                                                                                    skill
                                                                                }
                                                                                label={
                                                                                    skill
                                                                                }
                                                                                size='small'
                                                                                variant='outlined'
                                                                            />
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <Typography
                                                                        variant='body2'
                                                                        color='text.secondary'
                                                                    >
                                                                        No
                                                                        skills
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                        </Box>
                                    );
                                })}
                                {(!team?.members ||
                                    team.members.length === 0) && (
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ textAlign: 'center', py: 3 }}
                                    >
                                        No members yet.
                                    </Typography>
                                )}
                            </List>
                        </Box>
                    </TabPanel>
                </Box>
            </ContentContainer>

            {/* Mounted to support Edit / Delete from this page */}
            <TeamMemberDialog
                open={memberDialogOpen}
                mode={memberDialogMode}
                teamId={teamId}
                member={selectedMember}
                employeeOptions={employeeOptions}
                employeeOptionsLoading={employeeOptionsLoading}
                skillOptions={skillOptions}
                onClose={handleCloseMemberDialog}
            />
            <TeamDialog />
            <DeleteConfirmationDialog />
        </>
    );
};

export default TeamDetailsPage;
